/**
 * Centralized route constants for the PWA.
 *
 * Use ROUTES for static paths, DYNAMIC_ROUTES for parameterized paths,
 * and buildUrl when you need to append query parameters.
 */

// ─── Static Routes ───────────────────────────────────────────────────────────

export const ROUTES = {
  HOME: '/',
  SIGN_IN: '/sign-in',
  SIGN_OUT: '/sign-out',
  SIGN_IN_NATIVE: '/sign-in/native',
  REGISTER: '/register',
  REGISTER_MEMBER: '/register/member',
  REGISTER_INVITE_CODE: '/register/invite-code',
  REQUEST_INVITE: '/request-invite',
  ONBOARDING_REDIRECT: '/onboarding',
  ONBOARDING: '/v2/onboarding',
  ONBOARDING_V4: '/v4/onboarding',
  TERMS_OF_SERVICE: '/terms-of-service',
  AUTHENTICATE: '/authenticate',
  AUTHENTICATE_NATIVE: '/authenticate/native',
  DELETE_ACCOUNT: '/delete-account',
  TYPEFORM_SUBMITTED: '/typeform-submitted',
  ACCOUNT_CREATED: '/account-created',

  // verify
  VERIFY: '/verify',
  VERIFY_ACCESS: '/verify/access',
  VERIFY_ENTER_CONFIRMATION: '/verify/enter-confirmation',
  VERIFY_SUCCESS: '/verify/success',

  // referral
  REFERRAL: '/referral',
  REFERRAL_CONFIRM_EMAIL: '/referral/confirm-email',
  PARTNER_REGISTER: '/partner/register',

  // partner admin
  PARTNER_ADMIN: '/partner-admin',

  // portal / resources
  PORTAL: '/portal',
  PORTAL_SELF_REFERRAL: '/portal/self-referral',
  PORTAL_SELF_REFERRAL_SENT: '/portal/self-referral/sent',
  PORTAL_ADMIN_INVITE: '/portal/admin-invite',
  PORTAL_ADMIN_INVITE_SENT: '/portal/admin-invite/sent',
  PORTAL_CLIENT_REFERRAL: '/portal/client-referral',
  PORTAL_CLIENT_REFERRAL_LEAD_DATA: '/portal/client-referral/lead-data',
  PORTAL_CLIENT_REFERRAL_REVIEW: '/portal/client-referral/review',
  PORTAL_CLIENT_REFERRAL_SENT: '/portal/client-referral/sent',
  RESOURCES: '/resources',
  SETTINGS: '/settings',
  NOTIFICATIONS: '/notifications',
  AVATAR_EDITOR: '/settings/avatar',

  // organiser
  ORGANISER: '/organiser',
  // v2 member actions (per-care-circle); base redirects to the active circle
  V2_ACTIONS: '/v2/actions',
  KEY_DOCUMENTS: '/key-documents',
  DOCUMENT_UPLOAD: '/key-documents/upload',
  AUDIO_RECORD: '/key-documents/record-audio',
  CALLS: '/calls',

  // action plan
  ACTION_PLAN: '/action-plan',

  // care profile
  CARE_PROFILE: '/care-profile',
  CARE_PROFILE_START: '/care-profile/start',
  KEY_INFORMATION: '/key-information',

  // care assessment
  CARE_ASSESSMENT: '/care-assessment',
  CARE_ASSESSMENT_COMPLETE: '/care-assessment/complete',

  // care circle
  CARE_CIRCLE: '/care-circle',
  CARE_CIRCLE_NEW: '/care-circle/new',
  CARE_CIRCLE_INVITE: '/care-circle/invite',
  CIRCLE: '/circle',

  // attendance allowance
  ATTENDANCE_ALLOWANCE: '/attendance-allowance',
  ATTENDANCE_ALLOWANCE_START: '/attendance-allowance/start',
  ATTENDANCE_ALLOWANCE_COMPLETE: '/attendance-allowance/complete',
  ATTENDANCE_ALLOWANCE_SUCCESS: '/attendance-allowance/success',

  // financial status
  FINANCIAL_STATUS: '/financial-status',
  FINANCIAL_STATUS_COMPLETE: '/financial-status/complete',

  // eligibility calculator
  ELIGIBILITY_CALCULATOR: '/eligibility-calculator',
  ELIGIBILITY_CALCULATOR_COMPLETE: '/eligibility-calculator/complete',

  // eligibility calculator v2
  ELIGIBILITY_CALCULATOR_V2: '/v2/eligibility-calculator',
  ELIGIBILITY_CALCULATOR_V2_COMPLETE: '/v2/eligibility-calculator/complete',

  // sandbox
  SANDBOX: '/sandbox',

  // library
  LIBRARY: '/library',
  LIBRARY_BOOKMARKS: '/library/bookmarks',

  // book call
  BOOK_CALL: '/book-call',
  BOOK_CALL_DETAILS: '/book-call/details',

  // directory
  CARE_SEARCH: '/care-search',
  DIRECTORY: '/directory',
  DIRECTORY_CARE_HOME: '/directory/care-home',
  DIRECTORY_AT_HOME: '/directory/at-home',

  // services
  SERVICE: '/service',

  // webinars
  WEBINAR: '/webinar',

  // messenger
  MESSENGER: '/messenger',

  // zenco
  ZENCO_REDIRECT: '/zenco/redirect',

  // power of attorney
  POWER_OF_ATTORNEY: '/power-of-attorney', // home (entry: learn, guidance or apply)
  POA_PRICING: '/power-of-attorney/pricing', // calculator + zenco handoff
  POA_SUCCESS: '/poa-success',

  // admin
  ADMIN: '/admin',
  ADMIN_ACTION_PLAN: '/admin/action-plan',
  ADMIN_CARE_CIRCLE_MANAGE: '/admin/care-circle/manage',
  ADMIN_ACCOUNT_MANAGE: '/admin/account/manage',
  ADMIN_ACCOUNT_NEW: '/admin/account/new',
  ADMIN_INVITE_MANAGE: '/admin/invite/manage',
  ADMIN_ORGANIZATION_MANAGE: '/admin/organization/manage',
  ADMIN_ORGANIZATION_NEW: '/admin/organization/new',
  ADMIN_SERVICE_ACCOUNT: '/admin/service-account',
  ADMIN_REFERRING_PARTNER_MANAGE: '/admin/referring-partner/manage',
  ADMIN_REFERRING_PARTNER_NEW: '/admin/referring-partner/new',
  ADMIN_PARTNER_PRODUCT_MANAGE: '/admin/partner-product/manage',
  ADMIN_CASELOAD: '/admin/caseload',
  ADMIN_CALL_NOTE_NEW: '/admin/call-note/new',
  ADMIN_BACKTESTS: '/admin/backtests',
  ADMIN_BACKTEST_NEW: '/admin/backtests/new',
} as const;

// ─── Dynamic Routes ──────────────────────────────────────────────────────────

export const DYNAMIC_ROUTES = {
  V2_ACTIONS: (careCircleID = '', calendarID = '') =>
    `/v2/actions/${careCircleID}/${calendarID}`,
  REGISTER_ORGANIZATION: (organizationID = '') =>
    `/register/organization/${organizationID}`,
  LIBRARY_ARTICLE: (articleId = '') => `/library/${articleId}`,
  ACTION_PLAN_ITEM: (actionPlanItemId = '') =>
    `/action-plan/${actionPlanItemId}`,
  ACTION_PLAN_CATEGORY: (actionPlanCategoryId = '') =>
    `/action-plan/category/${actionPlanCategoryId}`,
  CARE_CIRCLE_MEMBER: (accountID = '') => `/care-circle/member/${accountID}`,
  CARE_ASSESSMENT_FORM: (careProfileFormId = '') =>
    `/care-assessment/${careProfileFormId}`,
  ATTENDANCE_ALLOWANCE_FORM: (careProfileFormId = '') =>
    `/attendance-allowance/${careProfileFormId}`,
  FINANCIAL_STATUS_FORM: (careProfileFormId = '') =>
    `/financial-status/${careProfileFormId}`,
  ELIGIBILITY_CALCULATOR_FORM: (careProfileFormId = '') =>
    `/eligibility-calculator/${careProfileFormId}`,
  ELIGIBILITY_CALCULATOR_V2_FORM: (careCircleID = '', chatSessionID = '') =>
    chatSessionID
      ? `/v2/eligibility-calculator/form/${careCircleID}/${chatSessionID}`
      : `/v2/eligibility-calculator/form/${careCircleID}`,
  SANDBOX_CHAT: (mode = '', chatSessionID = '') =>
    `/sandbox/chat/${mode}/${chatSessionID}`,
  CARE_PROFILE_CATEGORY_FORM: (categoryId = '', formId = '') =>
    `/${categoryId}/${formId}`,
  CARE_PROFILE_CATEGORY: (categoryId = '') => `/${categoryId}`,
  CARE_PROFILE_CATEGORY_SUCCESS: (categoryId = '') => `/${categoryId}/success`,
  SERVICE_DETAIL: (serviceId = '') => `/service/${serviceId}`,
  WEBINAR_DETAIL: (webinarId = '') => `/webinar/${webinarId}`,
  MESSENGER_CHAT: (chatId = '') => `/messenger/${chatId}`,
  DIRECTORY_PROVIDER: (providerID = '') => `/directory/providers/${providerID}`,
  ADMIN_ACCOUNT_VERIFY: (accountID = '') =>
    `/admin/account/verify/${accountID}`,
  ADMIN_CARE_CIRCLE_NEW: (accountID = '') =>
    `/admin/care-circle/new/${accountID}`,
  ADMIN_CARE_CIRCLE_VIEW: (careCircleID = '') =>
    `/admin/care-circle/view/${careCircleID}`,
  ADMIN_ORGANIZATION_EDIT: (organizationID = '') =>
    `/admin/organization/${organizationID}/edit`,
  ADMIN_ORGANIZATION_MEMBER: (organizationID = '') =>
    `/admin/organization/${organizationID}/member`,
  ADMIN_REFERRING_PARTNER_EDIT: (partnerID = '') =>
    `/admin/referring-partner/${partnerID}/edit`,
  ADMIN_CALL_NOTE_NEW_FOR_CIRCLE: (careCircleID = '') =>
    `/admin/call-note/new/${careCircleID}`,
  ADMIN_CALL_NOTES: (careCircleID = '') =>
    `/admin/care-circle/${careCircleID}/call-notes`,
  ADMIN_CALL_NOTE_EDIT: (careCircleID = '', documentID = '') =>
    `/admin/care-circle/${careCircleID}/call-notes/${documentID}/edit`,
  ADMIN_CALL_NOTE_VIEW: (careCircleID = '', documentID = '') =>
    `/admin/care-circle/${careCircleID}/call-notes/${documentID}`,
  CALL_NOTE_VIEW: (documentID = '') => `/call-notes/${documentID}`,
  SHORTLIST_DETAIL: (shortlistID = '') => `/care-search/${shortlistID}`,
  ADMIN_CASELOAD_FOR: (accountID = '') => `/admin/caseload/${accountID}`,
  ADMIN_BACKTEST: (backtestID = '') => `/admin/backtests/${backtestID}`,
} as const;

// ─── Route Helpers ───────────────────────────────────────────────────────────

export const wildcard = (route?: string) => (route ? `${route}/*` : '*');
export const trailingSlash = (route: string) => `${route}/`;

// ─── Query Param Helper ──────────────────────────────────────────────────────

export const buildUrl = (
  path: string,
  params?: Record<string, string | undefined>,
): string => {
  if (!params) return path;
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) searchParams.set(key, value);
  });
  const qs = searchParams.toString();
  return qs ? `${path}?${qs}` : path;
};
